import AmbientGlow from '../components/AmbientGlow';
/**
 * ArtistDashboardPage - Main dashboard for artists
 * Features: AI Label Manager, Fanbase Heatmap, Revenue Room, Release Countdowns, Fan Insights
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, Users, DollarSign, Music, TrendingUp, Calendar,
  Upload, Settings, ChevronRight, Play, Eye, Heart, MessageCircle,
  Globe2, Sparkles, Bell, ArrowUpRight, Zap, Target, 
  Headphones, Clock, Award, MapPin, FileText, Share2, Trash2, Crown, Video, Image, Wallet
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import MediaUpload from '../components/MediaUpload';
import PromoKitModal from '../components/PromoKitModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, change, changeType, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#12101A] rounded-2xl p-5 border border-white/5"
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      {change && (
        <span className={`text-xs font-medium flex items-center gap-1 ${
          changeType === 'up' ? 'text-green-400' : 'text-red-400'
        }`}>
          <TrendingUp className={`w-3 h-3 ${changeType === 'down' ? 'rotate-180' : ''}`} />
          {change}
        </span>
      )}
    </div>
    <p className="text-2xl font-black text-white mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>{value}</p>
    <p className="text-[#71717A] text-sm">{label}</p>
  </motion.div>
);

// Quick Action Card
const QuickActionCard = ({ icon: Icon, label, description, onClick, color, ...rest }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="bg-[#12101A] rounded-2xl p-5 border border-white/5 text-left hover:border-white/10 transition-colors w-full"
    {...rest}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h4 className="text-white font-semibold mb-1">{label}</h4>
    <p className="text-[#71717A] text-sm">{description}</p>
  </motion.button>
);

// Track Row Component with Delete
const TrackRow = ({ track, rank, onDelete, onPromote }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(track.id);
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors group">
      <span className="text-[#71717A] text-sm font-medium w-6">{rank}</span>
      <div className="w-12 h-12 rounded-lg bg-white/10 overflow-hidden flex-shrink-0">
        {track.artwork ? (
          <img src={track.artwork} alt={track.track_title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="w-5 h-5 text-[#71717A]" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{track.track_title}</p>
        <p className="text-[#71717A] text-sm">{track.genre || 'Unknown Genre'}</p>
      </div>
      <div className="flex items-center gap-4 text-[#71717A] text-sm">
        <span className="flex items-center gap-1">
          <Play className="w-4 h-4" />
          {track.play_count?.toLocaleString() || 0}
        </span>
        <span className="flex items-center gap-1">
          <Heart className="w-4 h-4" />
          {track.like_count?.toLocaleString() || 0}
        </span>
        {showConfirm ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-2 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {deleting ? '...' : 'Delete'}
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="px-2 py-1 bg-white/10 text-white text-xs rounded-lg hover:bg-white/20"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPromote && onPromote(track)}
              className="p-1.5 hover:bg-[#00F0FF]/20 rounded-lg transition-all"
              data-testid={`promote-track-${track.id}`}
              title="Promote & share"
            >
              <Share2 className="w-4 h-4 text-[#00F0FF]" />
            </button>
            <button
              onClick={() => setShowConfirm(true)}
              className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 rounded-lg transition-all"
              data-testid={`delete-track-${track.id}`}
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Heatmap Location Item
const HeatmapLocation = ({ location, listeners, percentage }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF007F] to-[#FF007F]/50 flex items-center justify-center">
      <MapPin className="w-5 h-5 text-white" />
    </div>
    <div className="flex-1">
      <p className="text-white font-medium">{location}</p>
      <p className="text-[#71717A] text-sm">{listeners.toLocaleString()} listeners</p>
    </div>
    <div className="w-24">
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF]"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  </div>
);

// AI Label Manager Card
const AILabelManagerCard = ({ onOpen }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gradient-to-br from-[#1a1625] to-[#12101A] rounded-2xl p-6 border border-[#FF007F]/20 relative overflow-hidden"
  >
    {/* Decorative Elements */}
    <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF007F]/10 rounded-full blur-3xl" />
    <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#00F0FF]/10 rounded-full blur-3xl" />
    
    <div className="relative z-10">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-white font-bold text-lg">AI Label Manager</h3>
          <p className="text-[#A1A1AA] text-sm">Your virtual A&R assistant</p>
        </div>
      </div>
      
      <p className="text-[#A1A1AA] text-sm mb-4 leading-relaxed">
        Get personalized insights on your music career, marketing strategies, 
        and growth opportunities powered by AI.
      </p>
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
          <Zap className="w-4 h-4 text-[#00F0FF]" />
          <span>Release timing recommendations</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
          <Target className="w-4 h-4 text-[#FF007F]" />
          <span>Audience growth strategies</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span>Performance analysis & tips</span>
        </div>
      </div>
      
      <button
        onClick={onOpen}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        data-testid="ai-label-manager-btn"
      >
        Open AI Manager
        <ArrowUpRight className="w-4 h-4" />
      </button>
    </div>
  </motion.div>
);

// Main Dashboard Component
// Store Setup Modal - create a product
const StoreSetupModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', description: '', price: '', category: 'merch', stock: '10', image: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError('');
    if (!form.name.trim() || !form.price || Number(form.price) <= 0) {
      setError('Please enter a name and a valid price.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/store/products`, {
        name: form.name.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        category: form.category,
        stock: Number(form.stock) || 0,
        images: form.image.trim() ? [form.image.trim()] : []
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCreated && onCreated();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create product.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
          className="w-full max-w-md bg-[#12101A] rounded-3xl border border-white/10 overflow-hidden max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()} data-testid="store-setup-modal">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-white font-bold text-lg">Add Product</h3>
            <button onClick={onClose} className="text-[#71717A] hover:text-white" data-testid="store-setup-close">✕</button>
          </div>
          <div className="p-6 space-y-4">
            {error && <div className="p-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-sm">{error}</div>}
            <input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Product name"
              className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B]" data-testid="product-name-input" />
            <textarea value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Description"
              rows={2} className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B]" data-testid="product-desc-input" />
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3">
                <span className="text-[#A1A1AA]">$</span>
                <input type="number" min="0.01" value={form.price} onChange={(e) => update('price', e.target.value)} placeholder="Price"
                  className="flex-1 bg-transparent py-3 text-white outline-none" data-testid="product-price-input" />
              </div>
              <input type="number" min="0" value={form.stock} onChange={(e) => update('stock', e.target.value)} placeholder="Stock"
                className="bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B]" data-testid="product-stock-input" />
            </div>
            <select value={form.category} onChange={(e) => update('category', e.target.value)}
              className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none" data-testid="product-category-select">
              <option value="merch">Merch</option>
              <option value="music">Music</option>
              <option value="tickets">Tickets</option>
            </select>
            <input value={form.image} onChange={(e) => update('image', e.target.value)} placeholder="Image URL (optional)"
              className="w-full bg-white/5 rounded-xl px-4 py-3 text-white outline-none placeholder-[#52525B]" data-testid="product-image-input" />
            <button onClick={submit} disabled={saving}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold disabled:opacity-50" data-testid="product-save-btn">
              {saving ? 'Creating...' : 'Create Product'}
            </button>
          </div>
        </motion.div>
      </div>
    </>
  );
};

const ArtistDashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [artistProfile, setArtistProfile] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [promoTrack, setPromoTrack] = useState(null);
  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAIManager, setShowAIManager] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [showStoreSetup, setShowStoreSetup] = useState(false);

  // Check if user is owner
  useEffect(() => {
    const checkOwner = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const res = await axios.get(`${API_URL}/api/admin/check-owner`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsOwner(res.data?.is_owner || false);
      } catch {
        setIsOwner(false);
      }
    };
    
    if (isAuthenticated) {
      checkOwner();
    }
  }, [isAuthenticated]);

  // Fetch artist data
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || authLoading) return;
      
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch profile, analytics, and tracks in parallel
        const [profileRes, analyticsRes, tracksRes, audienceRes, revenueRes] = await Promise.all([
          axios.get(`${API_URL}/api/artists/profile/me`, { headers }).catch(() => null),
          axios.get(`${API_URL}/api/artists/analytics/overview`, { headers }).catch(() => null),
          axios.get(`${API_URL}/api/artists/radio/analytics/my-tracks`, { headers }).catch(() => null),
          axios.get(`${API_URL}/api/artists/analytics/audience`, { headers }).catch(() => null),
          axios.get(`${API_URL}/api/store/artist/revenue/my`, { headers }).catch(() => null)
        ]);

        if (profileRes?.data?.profile) {
          setArtistProfile(profileRes.data.profile);
        }
        if (analyticsRes?.data) {
          setAnalytics(analyticsRes.data);
        }
        if (tracksRes?.data?.tracks) {
          setTracks(tracksRes.data.tracks);
        }
        if (audienceRes?.data) {
          setAudience(audienceRes.data);
        }
        if (revenueRes?.data?.success) {
          setRevenue(revenueRes.data);
        }
      } catch (error) {
        console.error('Error fetching artist data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, authLoading]);

  // Calculate top locations for heatmap
  const topLocations = useMemo(() => {
    if (!audience?.top_locations) return [];
    const maxCount = Math.max(...audience.top_locations.map(l => l.count));
    return audience.top_locations.slice(0, 5).map(loc => ({
      ...loc,
      percentage: (loc.count / maxCount) * 100
    }));
  }, [audience]);

  // Handle track deletion
  const handleDeleteTrack = async (trackId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/artists/radio/tracks/${trackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove track from local state
      setTracks(tracks.filter(t => t.id !== trackId));
    } catch (error) {
      console.error('Error deleting track:', error);
      alert('Failed to delete track. Please try again.');
    }
  };

  // Redirect if not authenticated
  if (!authLoading && !isAuthenticated) {
    navigate('/auth', { state: { from: { pathname: '/artist-dashboard' } } });
    return null;
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-[#FF007F] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // If user doesn't have an artist profile, show setup prompt
  if (!artistProfile) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-[#12101A] rounded-3xl p-8 text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
            <Music className="w-10 h-10 text-white" />
          </div>
          <h2 
            className="text-2xl font-bold text-white mb-3"
            style={{ fontFamily: 'Unbounded' }}
          >
            Create Your Artist Profile
          </h2>
          <p className="text-[#A1A1AA] mb-6">
            Set up your artist profile to access your dashboard, 
            upload music, and connect with fans.
          </p>
          <button
            onClick={() => navigate('/artist-setup')}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold hover:opacity-90 transition-opacity"
            data-testid="create-artist-profile-btn"
          >
            Create Artist Profile
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507]">
      <AmbientGlow />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-white hover:text-[#00F0FF] transition-colors"
            >
              <ChevronRight className="w-6 h-6 rotate-180" />
            </button>
            <div>
              <h1 
                className="text-2xl font-black text-white tracking-tight"
                style={{ fontFamily: 'Unbounded' }}
              >
                Artist Dashboard
              </h1>
              <p className="text-[#71717A] text-sm">{artistProfile.stage_name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isOwner && (
              <button 
                onClick={() => navigate('/owner-dashboard')}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium hover:opacity-90 transition-opacity"
                data-testid="owner-dashboard-btn"
              >
                <Crown className="w-4 h-4 text-yellow-400" />
                Owner
              </button>
            )}
            <button className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
              <Bell className="w-5 h-5 text-white" />
            </button>
            <button 
              onClick={() => navigate('/settings')}
              className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
            >
              <Settings className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 pb-24">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Play}
            label="Total Plays"
            value={analytics?.overview?.total_plays?.toLocaleString() || '0'}
            change="+12%"
            changeType="up"
            color="bg-gradient-to-br from-[#FF007F] to-[#FF007F]/50"
          />
          <StatCard
            icon={Users}
            label="Followers"
            value={analytics?.overview?.followers?.toLocaleString() || '0'}
            change="+8%"
            changeType="up"
            color="bg-gradient-to-br from-[#00F0FF] to-[#00F0FF]/50"
          />
          <StatCard
            icon={Music}
            label="Tracks"
            value={analytics?.overview?.total_tracks?.toString() || '0'}
            color="bg-gradient-to-br from-purple-500 to-purple-500/50"
          />
          <StatCard
            icon={DollarSign}
            label="Revenue"
            value={`$${analytics?.overview?.total_revenue?.toFixed(2) || '0.00'}`}
            change="+24%"
            changeType="up"
            color="bg-gradient-to-br from-green-500 to-green-500/50"
          />
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <div>
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
                Quick Actions
              </h3>
              <div className="grid sm:grid-cols-4 gap-4">
                <QuickActionCard
                  icon={Upload}
                  label="Upload Track"
                  description="Share new music"
                  onClick={() => navigate('/upload')}
                  color="bg-gradient-to-br from-[#FF007F] to-[#FF007F]/50"
                  data-testid="quick-action-upload"
                />
                <QuickActionCard
                  icon={Wallet}
                  label="Wallet & Cash Out"
                  description="Get paid"
                  onClick={() => navigate('/wallet')}
                  color="bg-gradient-to-br from-[#39FF14] to-[#39FF14]/50"
                  data-testid="quick-action-wallet"
                />
                <QuickActionCard
                  icon={FileText}
                  label="View EPK"
                  description="Your press kit"
                  onClick={() => navigate(`/epk/${encodeURIComponent(artistProfile?.stage_name)}`)}
                  color="bg-gradient-to-br from-purple-500 to-purple-500/50"
                  data-testid="quick-action-epk"
                />
                <QuickActionCard
                  icon={Calendar}
                  label="Schedule Release"
                  description="Plan your drops"
                  onClick={() => navigate('/schedule-release')}
                  color="bg-gradient-to-br from-[#00F0FF] to-[#00F0FF]/50"
                  data-testid="quick-action-schedule"
                />
                <QuickActionCard
                  icon={BarChart3}
                  label="View Analytics"
                  description="Deep dive stats"
                  onClick={() => navigate('/analytics')}
                  color="bg-gradient-to-br from-green-500 to-green-500/50"
                  data-testid="quick-action-analytics"
                />
              </div>
            </div>

            {/* Top Tracks */}
            <div className="bg-[#12101A] rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#FF007F]" />
                  Top Performing Tracks
                </h3>
                <button 
                  onClick={() => navigate('/analytics')}
                  className="text-[#00F0FF] text-sm hover:underline"
                >
                  View All
                </button>
              </div>
              
              <div className="space-y-1">
                {tracks.length > 0 ? (
                  tracks.slice(0, 5).map((track, index) => (
                    <TrackRow 
                      key={track.id} 
                      track={track} 
                      rank={index + 1}
                      onDelete={handleDeleteTrack}
                      onPromote={setPromoTrack}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-[#71717A]">
                    <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No tracks uploaded yet</p>
                    <button
                      onClick={() => navigate('/upload')}
                      className="mt-3 text-[#00F0FF] hover:underline"
                    >
                      Upload your first track
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Fanbase Heatmap */}
            <div className="bg-[#12101A] rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Globe2 className="w-5 h-5 text-[#00F0FF]" />
                  Fanbase Heatmap
                </h3>
                <span className="text-[#71717A] text-sm">
                  {audience?.total_followers || 0} total fans
                </span>
              </div>
              
              <div className="space-y-3">
                {topLocations.length > 0 ? (
                  topLocations.map((loc, index) => (
                    <HeatmapLocation
                      key={index}
                      location={loc.location}
                      listeners={loc.count}
                      percentage={loc.percentage}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-[#71717A]">
                    <Globe2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Fanbase data will appear as you grow</p>
                  </div>
                )}
              </div>
            </div>

            {/* Media Upload Section */}
            <div className="bg-[#12101A] rounded-2xl p-5 border border-white/5">
              <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                <Video className="w-5 h-5 text-purple-400" />
                Upload Videos & Photos
              </h3>
              <p className="text-[#71717A] text-sm mb-4">
                Share music videos, behind-the-scenes footage, and photos with your fans.
                Videos can be up to 10 minutes long.
              </p>
              <MediaUpload 
                onUploadComplete={(media) => {
                  console.log('Media uploaded:', media);
                }}
              />
            </div>
          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-6">
            {/* AI Label Manager */}
            <AILabelManagerCard onOpen={() => setShowAIManager(true)} />

            {/* Revenue Room */}
            <div className="bg-[#12101A] rounded-2xl p-5 border border-white/5">
              <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-green-400" />
                Revenue Room
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <Headphones className="w-5 h-5 text-[#00F0FF]" />
                    <span className="text-[#A1A1AA]">Streaming</span>
                  </div>
                  <span className="text-white font-medium">${(revenue?.streaming || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <Award className="w-5 h-5 text-[#FF007F]" />
                    <span className="text-[#A1A1AA]">Tips & Donations</span>
                  </div>
                  <span className="text-white font-medium" data-testid="revenue-tips">${(revenue?.tips || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <Music className="w-5 h-5 text-purple-400" />
                    <span className="text-[#A1A1AA]">Sales</span>
                  </div>
                  <span className="text-white font-medium" data-testid="revenue-sales">${(revenue?.sales || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-green-500/10 to-transparent border border-green-500/20">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    <span className="text-white font-semibold">Net Payout</span>
                  </div>
                  <span className="text-green-400 font-bold" data-testid="revenue-net-payout">${(revenue?.net_payout || 0).toFixed(2)}</span>
                </div>
                <p className="text-[#52525B] text-xs text-center">
                  After {revenue?.platform_fee_percentage ?? 15}% platform fee (${(revenue?.platform_fee_paid || 0).toFixed(2)} paid)
                </p>
              </div>

              <button
                onClick={() => navigate('/wallet')}
                className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold transition-opacity hover:opacity-90 text-sm flex items-center justify-center gap-2"
                data-testid="open-wallet-btn"
              >
                <Wallet className="w-4 h-4" /> Wallet &amp; Cash Out
              </button>
              
              <div className="grid grid-cols-2 gap-2 mt-4">
                <button
                  onClick={() => setShowStoreSetup(true)}
                  className="py-3 rounded-xl border border-white/10 text-white hover:bg-white/5 transition-colors text-sm"
                  data-testid="setup-store-btn"
                >
                  Add Product
                </button>
                <button
                  onClick={() => navigate('/store-manager')}
                  className="py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-medium transition-opacity hover:opacity-90 text-sm"
                  data-testid="manage-store-btn"
                >
                  Manage Store
                </button>
              </div>
            </div>

            {/* Fan Insights */}
            <div className="bg-[#12101A] rounded-2xl p-5 border border-white/5">
              <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-purple-400" />
                Fan Insights
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#A1A1AA] text-sm">Profile Views</span>
                  <span className="text-white font-medium">{artistProfile.profile_views || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#A1A1AA] text-sm">New Followers (30d)</span>
                  <span className="text-white font-medium">{audience?.new_followers_30d || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#A1A1AA] text-sm">Avg. Listen Time</span>
                  <span className="text-white font-medium">2:34</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#A1A1AA] text-sm">Save Rate</span>
                  <span className="text-white font-medium">24%</span>
                </div>
              </div>
            </div>

            {/* Release Countdown */}
            <div className="bg-[#12101A] rounded-2xl p-5 border border-white/5">
              <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-[#00F0FF]" />
                Upcoming Releases
              </h3>
              
              <div className="text-center py-6 text-[#71717A]">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No scheduled releases</p>
                <button
                  onClick={() => navigate('/schedule-release')}
                  className="mt-3 text-[#00F0FF] text-sm hover:underline"
                >
                  Schedule a release
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* AI Manager Modal */}
      <AnimatePresence>
        {showAIManager && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-md z-50"
              onClick={() => setShowAIManager(false)}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="w-full max-w-2xl bg-[#12101A] rounded-3xl border border-white/10 overflow-hidden max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">AI Label Manager</h3>
                      <p className="text-[#71717A] text-sm">Your virtual A&R assistant</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAIManager(false)}
                    className="text-[#71717A] hover:text-white transition-colors"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="p-6">
                  <div className="text-center py-12">
                    <Sparkles className="w-16 h-16 mx-auto mb-4 text-[#FF007F]" />
                    <h4 className="text-white font-bold text-xl mb-2">AI Features Coming Soon</h4>
                    <p className="text-[#A1A1AA] max-w-sm mx-auto">
                      Get personalized career insights, release timing recommendations, 
                      and growth strategies powered by AI.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Store Setup Modal */}
      <AnimatePresence>
        {showStoreSetup && (
          <StoreSetupModal
            onClose={() => setShowStoreSetup(false)}
            onCreated={() => {
              const token = localStorage.getItem('token');
              axios.get(`${API_URL}/api/store/artist/revenue/my`, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => { if (r?.data?.success) setRevenue(r.data); })
                .catch(() => {});
            }}
          />
        )}
      </AnimatePresence>

      {promoTrack && (
        <PromoKitModal open={!!promoTrack} type="track" id={promoTrack.id} onClose={() => setPromoTrack(null)} />
      )}
    </div>
  );
};

export default ArtistDashboardPage;
